import { FC, Fragment, useEffect } from "react";
import {
  CancellationOfSharesModel,
  ReductionOfShareCapitalForm,
  ShareCapitalReductionType,
} from "./reductionOfShareCapitalDomain";
import {
  ArticleSelectorQuestion,
  DateQuestion,
  NumberQuestion,
  YesNoQuestion,
} from "@/dbot/common/commonComponents";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import { observer } from "mobx-react";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import styles from "@/dbot/common/dbot.module.css";
import { MTextInput } from "@/components-v2/TextInput";
import { LinkButton } from "@/components-v2/Button";
import { Dialog } from "@/components-v2/Dialog";
import { Alert } from "@/components-v2/Alert";
import { MCheckboxGroup } from "@/components-v2/Checkbox";
import { CURRENCY_NOK } from "@/utils/currency";
import { Purpose } from "@/shareholderRegister/transactions/commonCapitalReduction";
import { SpecialCircumstancesType } from "@/common/common";
import { useTranslation } from "@/stores/RootStoreContext";

import { InfoButton } from "@/components-v2/InfoButton";
import { findShareTypeName } from "@/common/share";
import { MDateInput } from "@/components-v2/DateInput";
import { ChangeArticlesOfAssociation } from "@/dbot/changeArticlesOfAssociation/changeArticlesOfAssociationComponents";
import { UpdateArticlesAutomatically } from "@/dbot/increaseOfShareCapital/increaseOfShareCapitalComponents";

export const ReductionOfShareCapitalView: FC<{ form: ReductionOfShareCapitalForm }> = observer(
  ({ form }) => {
    const { t } = useTranslation();
    const isLocked = form.context.isLocked;
    useEffect(() => {
      form.init();
    }, [form]);

    return (
      <VBox gap={"md"}>
        <div className={"text-base"}>{t("reductionOfShareCapital.balancesheet.questionText")}</div>
        <MRadioButtonGroup model={form.annualAccountInterimBalanceSheetModel} horizontal />
        <MDateInput
          label={
            form.annualAccountInterimBalanceSheetModel.isSelected(
              ShareCapitalReductionType.INTERIM_BALANCE_SHEET,
            )
              ? t("reductionOfShareCapital.dateOfDetermination.interimBalanceSheet")
              : t("reductionOfShareCapital.dateOfDetermination.annualAccounts")
          }
          model={form.dateOfDetermination}
          disabled={isLocked}
        />
        {form.isDateOfDeterminationLastDayOfYear && (
          <Alert
            type={"info"}
            message={t("reductionOfShareCapital.dateOfDetermination.lastDayOfYear")}
          />
        )}
        {form.annualAccountInterimBalanceSheetModel.isSelected(
          ShareCapitalReductionType.INTERIM_BALANCE_SHEET,
        ) && (
          <DateQuestion
            question={t("reductionOfShareCapital.intermediateBalanceSheetDate.questionText")}
            field={form.intermediateBalanceSheetDateModel}
            isLocked={isLocked}
          />
        )}
        <ReductionOfShareCapitalMethod form={form} />
        {form.isReductionOfNominalValue && <ReductionOfNominalValueView form={form} />}
        {form.isCancellationOfShares && (
          <CancellationOfSharesView model={form.cancellationOfSharesModel} />
        )}
        <ReductionPurposeView form={form} />
        <hr />
        <YesNoQuestion
          question={t("reductionOfShareCapital.subsequentCapitalIncrease.questionText")}
          field={form.isSubsequentCapitalIncreaseModel}
          isLocked={isLocked}
        />
        <EstimatedCostsView form={form} />
        <CircumstancesView form={form} />
        {!form.isAlsoDealingWithAnnualAccounts && (
          <CircumstancesMaterialImportanceView form={form} />
        )}
        <ArticlesChangeView form={form} />
      </VBox>
    );
  },
);

const ReductionOfShareCapitalMethod: FC<{ form: ReductionOfShareCapitalForm }> = observer(
  ({ form }) => {
    const { t } = useTranslation();
    return (
      <VBox>
        <div className="text-base">{t("reductionOfShareCapital.reductionType.questionText")}</div>
        <MRadioButtonGroup model={form.reductionTypeRadioModel} disabled={form.context.isLocked} />
      </VBox>
    );
  },
);

const ReductionOfNominalValueView: FC<{ form: ReductionOfShareCapitalForm }> = observer(
  ({ form }) => {
    const { t, lang } = useTranslation();
    const reductionInNominalValue =
      form.reductionOfNominalValueModel.reductionInNominalValue.toNumberOrZero();
    return (
      <VBox>
        <NumberQuestion
          question={t("reductionOfShareCapital.reductionInNominalValue.questionText")}
          prefix={CURRENCY_NOK}
          field={form.reductionOfNominalValueModel.reductionInNominalValue}
          isLocked={form.context.isLocked}
        />
        <Grid cols={3}>
          <GridItem colSpan={3}>
            <Grid cols={3} className={styles.tableHeader}>
              <Box key={"header"} />
              <Box justifyContent="center" key={"before"}>
                {t("before")}
              </Box>
              <Box justifyContent="center" key={"after"}>
                {t("after")}
              </Box>
            </Grid>
          </GridItem>

          <Box key="amount" justifyContent={"end"}>
            {t("transactions.issueTransaction.nominal-amount")}
          </Box>
          <Box key="currentnominalvalue" justifyContent={"center"}>
            {form.currentNominalValue.toLocaleString(lang)}
          </Box>
          <Box key="reductionValue" justifyContent={"center"}>
            {form.calculateReductionInNominalvalueForPreview.toLocaleString(lang)}
          </Box>

          <Box key="numberofshares" justifyContent={"end"}>
            {t("decisions.increaseShareCapitalAgainstCash.numberOfShares")}
          </Box>
          <Box key="currentNrShares" justifyContent={"center"}>
            {form.currentNumberOfShares.toLocaleString(lang)}
          </Box>
          <Box key="numberOfsharesx" justifyContent={"center"}>
            {form.currentNumberOfShares.toLocaleString(lang)}
          </Box>

          <Box key="capital" justifyContent={"end"}>
            {t("shareClasses.capital")}
          </Box>
          <Box justifyContent={"center"}>
            {form.calculateCurrentSharecapitalForPreview.toLocaleString(lang)}
          </Box>

          <Box key="capitalafter" justifyContent={"center"}>
            {form.calculateSharecapitalAfterForPreview.toLocaleString(lang)}
          </Box>

          <Box key="reductionSharesclasses" justifyContent={"end"}>
            {t("shareClasses.reduction")}
          </Box>
          <Box key={"empty"} />
          <Box key="reductionValueAfter" justifyContent={"center"}>
            {reductionInNominalValue > 0 &&
              "-" + form.calculateSharecapitalReductionAfterForPreview.toLocaleString(lang)}
          </Box>
        </Grid>
      </VBox>
    );
  },
);

const CancellationOfSharesView: FC<{ model: CancellationOfSharesModel }> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <VBox paddingY={"sm"}>
      <div className="text-base">{t("reductionOfShareCapital.cancellation.questionText")}</div>
      <MRadioButtonGroup model={model.cancellationMethodModel} disabled={model.isLocked} />
      {model.isCancellationMethodAllSharesInShareClass && (
        <CancellationOfAllSharesInShareClassView model={model} />
      )}
      {model.isCancellationMethodCertainShares && <CancellationOfCertainSharesView model={model} />}
    </VBox>
  );
});

const CancellationOfAllSharesInShareClassView: FC<{ model: CancellationOfSharesModel }> = observer(
  ({ model }) => {
    const { t, lang } = useTranslation();
    return (
      <>
        <div className={"text-base"}>{t("reductionOfShareCapital.selectShareClasses")}</div>
        {model.allShareClassesSelected && (
          <Alert type={"warning"} message={t("reductionOfShareCapital.allShareClassesSelected")} />
        )}
        <MCheckboxGroup
          model={model.cancellationOfAllSharesInShareClassModel}
          labelFn={(shareType) => findShareTypeName(shareType, lang)}
        />
      </>
    );
  },
);
const CancelSharesSpecificationDialog: FC<{ model: CancellationOfSharesModel }> = observer(
  ({ model }) => {
    const { t, lang } = useTranslation();
    return (
      <Dialog
        submitLabel={t("save")}
        title={t("reductionOfShareCapital.changeSpecification")}
        onSubmit={() => model.saveCancelCertainSharesSpecification()}
        onCancel={() => model.toggleSpecificationDialog()}
      >
        <table>
          <thead>
            <tr>
              <th></th>
              {model.cancellationsForShareClasses?.map((c) => (
                <th key={c.sharetypeId}>{findShareTypeName(c.shareType, lang)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td></td>
              {model.cancellationsForShareClasses?.map((c) => (
                <td key={c.sharetypeCode}>
                  <Box justifyContent="center">
                    {c.numberOfSharesToBeCancelled.parsedValue ?? 0}
                  </Box>
                </td>
              ))}
            </tr>
            {model.shareholders.map((shareholder) => (
              <tr key={shareholder.id}>
                <td>{shareholder.name}</td>
                {model.cancellationsForShareClasses?.map((c) => {
                  const numberModel = c.getCancellationForShareholder(
                    shareholder.id,
                    c.sharetypeId,
                  );
                  return (
                    <td key={c.sharetypeCode}>
                      {numberModel && (
                        <Box>
                          <MTextInput model={numberModel.numberOfSharesToBeCancelled} />
                        </Box>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr>
              <td />
              {model.cancellationsForShareClasses?.map((c) => (
                <td key={c.sharetypeId}>
                  {c.getTotal()} / {c.numberOfSharesToBeCancelled.parsedValue}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
        {model.cancellationsForShareClasses
          ?.filter((c) => !c.isCancelDistributionValid)
          .map((c) => (
            <Alert
              key={c.sharetypeId}
              type={"error"}
              message={t("reductionOfShareCapital.errorInCertainSharesDistribution", {
                shareclass: findShareTypeName(c.shareType, lang),
              })}
            />
          ))}
      </Dialog>
    );
  },
);

const CancellationOfCertainSharesView: FC<{ model: CancellationOfSharesModel }> = observer(
  ({ model }) => {
    const { t, lang } = useTranslation();
    return (
      <VBox>
        <b>{t("reductionOfShareCapital.chooseShareClasses")}</b>
        <Grid inline cols={2} alignItems={"center"}>
          {model.cancellationsForShareClasses?.map((c) => (
            <Fragment key={c.sharetypeId}>
              <Box marginX={"sm"} grow textAlign={"right"} justifyContent={"end"}>
                {findShareTypeName(c.shareType, lang)}
              </Box>
              <MTextInput
                placeholder={t("reductionOfShareCapital.numberOfSharesToBeCancelled")}
                model={c.numberOfSharesToBeCancelled}
                autofocus
              />
            </Fragment>
          ))}
        </Grid>
        <VBox>
          {t("reductionOfShareCapital.changeSpecification")}
          <Box>
            {model.isSpecificationDialogOpen && <CancelSharesSpecificationDialog model={model} />}
          </Box>
          <Grid cols={3}>
            {model.cancellationForShareholdersForDisplay?.map((s) => {
              return (
                <Fragment key={s.shareholderId + "-" + s.sharetypeId}>
                  <div className={"text-sm"}>{s.shareholderName}</div>
                  <div className={"text-sm"}>{findShareTypeName(s.shareType, lang)}</div>
                  <div className={"text-sm"}>{s.numberOfSharesToBeCancelled.inputValue}</div>
                </Fragment>
              );
            })}
          </Grid>
          <Box>
            <LinkButton
              label={t("edit")}
              onClick={() => model.toggleSpecificationDialog()}
              disabled={model.isLocked}
            />
            {model.displayPreviewOfCancellForShareClasses && (
              <LinkButton
                label={model.showAllCertainSharesCancellation ? t("showExtract") : t("showAll")}
                onClick={() => model.toggleShowAllCertainSharesCancellation()}
              />
            )}
          </Box>
        </VBox>
      </VBox>
    );
  },
);

const ReductionPurposeView: FC<{ form: ReductionOfShareCapitalForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <>
      <VBox>
        <div className={"text-base"}>{t("reductionOfShareCapital.reductionPurpose")}</div>
        <MCheckboxGroup
          model={form.reductionPurposeModel}
          onChange={(_, purpose) => form.toggleReductionPurpose(purpose)}
          disabled={form.context.isLocked}
        />
      </VBox>
      {form.showCoverOfLossAmount && <CoverOfLossAmount form={form} />}
      {form.showDistributeToShareholders && <DistributionToShareholdersView form={form} />}
      {form.showTransferToOtherFunds && <TransferToOtherFunds form={form} />}
      {form.showSummaryForPurpose && <SummaryReductionPurposeView form={form} />}
      {form.isDistributionToShareholders && <AmountToBePaidOut form={form} />}
    </>
  );
});

const EstimatedCostsView: FC<{ form: ReductionOfShareCapitalForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <div className={"text-base"}>{t("reductionOfShareCapital.estimatedCosts")}</div>
      <MTextInput model={form.estimatedCostsModel} readOnly={form.context.isLocked} />
    </VBox>
  );
});

const SummaryReductionPurposeView: FC<{ form: ReductionOfShareCapitalForm }> = observer(
  ({ form }) => {
    const { t, lang } = useTranslation();
    const isCoverOfLoss = form.reductionPurposeModel.selected.includes(Purpose.COVER_OF_LOSS);
    const isDistribution = form.reductionPurposeModel.selected.includes(Purpose.DISTRIBUTION);
    const isCancellationOfOwn = form.reductionPurposeModel.selected.includes(
      Purpose.CANCELLATION_OF_OWN,
    );
    const isTransfer = form.reductionPurposeModel.selected.includes(Purpose.TRANSFER);
    return (
      <VBox>
        <div className={"text-base"}>{t("reductionOfShareCapital.summaryReductionPurpose")}</div>
        <Grid container cols={2}>
          {isCoverOfLoss && (
            <div className={"text-sm"}>
              {t("transactions.capitalReduction.purposeLabels.COVER_OF_LOSS")}
            </div>
          )}
          {isCoverOfLoss && (
            <div className={"text-sm"}>
              {form.coverOfLossAmountModel.toNumberOrZero().toLocaleString(lang)}
            </div>
          )}
          {isDistribution && (
            <div className={"text-sm"}>
              {t("transactions.capitalReduction.purposeLabels.DISTRIBUTION_TO_SHAREHOLDERS")}
            </div>
          )}
          {isDistribution && (
            <div className={"text-sm"}>
              {form.distributeToShareholdersModel.toNumberOrZero().toLocaleString(lang)}
            </div>
          )}
          {isCancellationOfOwn && (
            <div className={"text-sm"}>
              {t("transactions.capitalReduction.purposeLabels.CANCELLATION_OWN_SHARES")}
            </div>
          )}
          {isCancellationOfOwn && (
            <div className={"text-sm"}>
              {form.calculateCancellationOfOwnSharesAmount.toLocaleString(lang)}
            </div>
          )}
          {isTransfer && (
            <div className={"text-sm"}>
              {t("transactions.capitalReduction.purposeLabels.TRANSFER_OTHER_EQUITY")}
            </div>
          )}
          {isTransfer && (
            <div className={"text-sm"}>
              {form.transferToOtherFundsModel.toNumberOrZero().toLocaleString(lang)}
            </div>
          )}

          <div className={"text-base"}>{t("transactions.capitalReduction.totalAmount")}:</div>
          <div>
            {form.reductionSummary.specifiedPurposeAmount.toLocaleString(lang)} /{" "}
            {form.reductionSummary.reduction.toLocaleString(lang)}
          </div>
          {!form.reductionSummary.addsUp && (
            <GridItem colSpan={2}>
              <Alert type={"warning"} message={t("reductionOfShareCapital.purposeNotAddsUp")} />
            </GridItem>
          )}
        </Grid>
      </VBox>
    );
  },
);

const CoverOfLossAmount: FC<{ form: ReductionOfShareCapitalForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <div className={"text-base"}>{t("reductionOfShareCapital.coverOfLoss")}</div>
      <MTextInput model={form.coverOfLossAmountModel} />
    </VBox>
  );
});

const DistributionToShareholdersView: FC<{ form: ReductionOfShareCapitalForm }> = observer(
  ({ form }) => {
    const { t } = useTranslation();
    return (
      <VBox>
        <div className={"text-base"}>{t("reductionOfShareCapital.distributionToShareholders")}</div>
        <MTextInput model={form.distributeToShareholdersModel} />
      </VBox>
    );
  },
);

const TransferToOtherFunds: FC<{ form: ReductionOfShareCapitalForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <div className={"text-base"}>{t("reductionOfShareCapital.transferToOtherFunds")}</div>
      <MTextInput model={form.transferToOtherFundsModel} />
    </VBox>
  );
});

const ArticlesChangeView: FC<{ form: ReductionOfShareCapitalForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <>
      <UpdateArticlesAutomatically
        updateArticlesAutomatically={form.updateArticlesAutomatically}
        isProjectLocked={form.context.isLocked}
      />
      {form.isUpdateArticlesAutomatically && (
        <ArticleSelectorQuestion
          question={t("reductionOfShareCapital.articlesChangeSection")}
          model={form.articleOfAssociationNumberModel}
          isLocked={form.context.isLocked}
        />
      )}
      {form.isUpdateArticlesManually && (
        <ChangeArticlesOfAssociation model={form.articleChangeModel} />
      )}
    </>
  );
});

const CircumstancesView: FC<{ form: ReductionOfShareCapitalForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <div className={"text-base"}>{t("reductionOfShareCapital.circumstances.question")}</div>
      <MRadioButtonGroup model={form.circumstancesModel} disabled={form.context.isLocked} />
      {form.circumstancesModel.value === SpecialCircumstancesType.ADDED_EXPLANATION && (
        <>
          {form.documentLanguages.map((d) => (
            <div key={d}>
              {form.documentLanguages.length > 1 ? <div>{t(`language.${d}`)}</div> : null}
              <MTextInput
                model={form.cirumstancesExplanationModelFor(d)}
                readOnly={form.context.isLocked}
              />
            </div>
          ))}
        </>
      )}
    </VBox>
  );
});

const CircumstancesMaterialImportanceView: FC<{ form: ReductionOfShareCapitalForm }> = observer(
  ({ form }) => {
    const { t } = useTranslation();
    return (
      <VBox>
        <div className={"text-base"}>
          {t("reductionOfShareCapital.circumstancesMaterialImportance.question")}
        </div>
        <MRadioButtonGroup
          model={form.circumstancesMaterialImportanceModel}
          disabled={form.context.isLocked}
        />
        {form.circumstancesMaterialImportanceModel.value ===
          SpecialCircumstancesType.ADDED_EXPLANATION && (
          <>
            {form.documentLanguages.map((d) => (
              <div key={d}>
                {form.documentLanguages.length > 1 ? <div>{t(`language.${d}`)}</div> : null}
                <MTextInput
                  model={form.circumstancesMaterialImportanceExplanationModelFor(d)}
                  readOnly={form.context.isLocked}
                />
              </div>
            ))}
          </>
        )}
      </VBox>
    );
  },
);

const AmountToBePaidOut: FC<{ form: ReductionOfShareCapitalForm }> = observer(({ form }) => {
  const { t, lang } = useTranslation();
  return (
    <VBox paddingY={"sm"}>
      {form.affectedShareTypes.map((shareType) => (
        <>
          <div className={"text-base"}>
            {form.isCancellationOfShares
              ? t("reductionOfShareCapital.totalAmountPerCancelledShare", {
                  sharetype: findShareTypeName(shareType, lang, { singular: true }),
                })
              : t("reductionOfShareCapital.amountPerCancelledShare", {
                  sharetype: findShareTypeName(shareType, lang, { singular: true }),
                })}
            <InfoButton
              infoText={
                form.isCancellationOfShares
                  ? t("reductionOfShareCapital.amountToBePaidOutPerShare.guidance.cancellation")
                  : t(
                      "reductionOfShareCapital.amountToBePaidOutPerShare.guidance.nominalValueReduction",
                    )
              }
            />
          </div>
          <MTextInput
            key={shareType.sharetypeId}
            model={form.getAmountModelForSharetype(shareType.sharetypeId)}
          />
        </>
      ))}
      {form.isReductionOfNominalValue && (
        <YesNoQuestion
          question={t("reductionOfShareCapital.isRepayment.question")}
          field={form.isRepaymentOfPaidInCapitalModel}
          isLocked={form.context.isLocked}
        />
      )}
    </VBox>
  );
});
